import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useSearchParams,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import "./styles/index.css";
import DevelopersWebhooksCreate from "./components/Developers/DevelopersWebhooksCreate";
import { AppLayout } from "./components/ui/app-layout";
import DevelopersBase from "./components/Developers/DevelopersBase";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsProvider } from "./PermissionsProvider";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useAuthToken } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Tutorial from "./components/Tutorial/Tutorial";
import Events from "./components/Event/Events";
import Applications from "./components/Application/Applications";
import ApplicationOverview from "./components/Application/ApplicationOverview";
import RegisterApplication from "./components/Application/RegisterApplication";
import EditApplication from "./components/Application/EditApplication";
import CloneApplication from "./components/Application/CloneApplication";
import RegisterSentinel from "./components/Sentinel/RegisterSentinel";
import EditSentinel from "./components/Sentinel/EditSentinel";
import ApplicationActionOverview from "./components/Application/ApplicationActionOverview";
import ApplicationActionNew from "./components/Agent/AgentActionNew";
import Sandbox from "./components/Sandbox/Sandbox";
import { CompanyService } from "./services/CompanyService";
import { Company } from "./model_gen/company";
import { Button } from "./components/ui/button";
import SessionOverview from "./components/Session/SessionOverview";
import BillingBase from "./components/Billing/BillingBase";
import AnnotationOverview from "./components/Annotation/AnnotationOverview";
import AnnotationView from "./components/Annotation/AnnotationView";
import EmailOverview from "./components/Email/EmailOverview";
import RequestOverviewScreen from "./components/Request/RequestOverviewScreen";
import SentinelOverview from "./components/Admin/SentinelOverview";
import EvaluationHome from "./components/Evaluation/EvaluationHome";
import EvaluationNew from "./components/Evaluation/EvaluationNew";
import EvaluationDetail from "./components/Evaluation/EvaluationDetail";
import ValidationRun from "./components/Finetune/Validation/ValidationRunView";
import TestSets from "./components/TestSet/TestSets";
import TestSetDetail from "./components/TestSet/TestSetDetail";
import WorkflowTestSetDetail from "./components/TestSet/WorkflowTestSetDetail";
import WorkflowTestSetEdit from "./components/TestSet/WorkflowTestSetEdit";
import TestSetNew from "./components/TestSet/TestSetNew";
import TestRunNew from "./components/TestRun/TestRunNew";
import WorkflowTestRunNew from "./components/TestRun/WorkflowTestRunNew";
import WorkflowTestRunDetail from "./components/TestRun/WorkflowTestRunDetail";
import TestRunDetail from "./components/TestRun/TestRunDetail";
import TestSetEdit from "./components/TestSet/TestSetEdit";
import IntentGroupsOverview from "./components/Intent/IntentGroupsOverview";
import IntentGroupOverview from "./components/Intent/IntentGroupOverview";
import AgentTaskView from "./components/Agent/AgentTaskView";
import Datasets from "./components/Finetune/Compositions/Compositions";
import DatasetNew from "./components/Finetune/Datasets/DatasetNew";
import DatasetDetail from "./components/Finetune/Datasets/DatasetDetail";
import ReviewDetail from "./components/Finetune/Reviews/ReviewDetail";
import DatasetEdit from "./components/Finetune/Datasets/DatasetEdit";
import CompositionNew from "./components/Finetune/Compositions/CompositionNew";
import CompositionDetailView from "./components/Finetune/Compositions/CompositionDetailView";
import CompositionEdit from "./components/Finetune/Compositions/CompositionEdit";
import NewFinetuneRun from "./components/Finetune/FinetuneRun/NewFinetuneRun";
import FinetuneRuns from "./components/Finetune/FinetuneRun/FinetuneRuns";
import FinetuneRunDetail from "./components/Finetune/FinetuneRun/FinetuneRunDetail";
import Models from "./components/Finetune/Models";
import ValidationOverview from "./components/Finetune/Validation/ValidationOverview";
import IngestData from "./components/Admin/IngestData";
import AugmentationDetail from "./components/Finetune/Datasets/AugmentationDetail";
import Evaluations from "./components/Evaluations/Evaluations";
import CriteriaPage from "./components/Criteria/CriteriaPage";
import ForgeOverview from "./components/Forge/ForgeOverview";
import ConversationTree from "./components/Admin/ConversationTree/components/ConversationTree";
import ConversationTrees from "./components/Admin/ConversationTrees";
import ConversationTreeExecution from "./components/Admin/ConversationTreeExecution";
import AgentUptimeMetrics from "./components/Admin/AgentUptimeMetrics";
import VastInstancesOverview from "./components/Admin/VastInstancesOverview";
import BaseModelCandidatesOverview from "./components/Admin/BaseModelCandidatesOverview";
import Agents from "./components/Forge/Agents";
import Workflows from "./components/Forge/Workflows";
import WorkflowNew from "./components/Forge/WorkflowNew";
import WorkflowDetail from "./components/Forge/WorkflowDetail";
import AgentDetail from "./components/Agent/AgentOverview";
import AgentActionOverview from "./components/Agent/AgentActionOverview";
import AgentActionEditPage from "./components/Agent/AgentActionEditPage";
import BulkAPIImportPage from "./components/Agent/BulkAPIImportPage";
import AgentBuilder from "./components/Agent/AgentBuilder";
import ApplicationAgentsOverview from "./components/Forge/ApplicationAgentsOverview";
import AgentIntentManagement from "./components/Agent/AgentIntentManagement";
import StudioOverview from "./components/Studio/StudioOverview";
import LoginChooser from "./components/Auth/LoginChooser";
import OrganizationSignup from "./components/Auth/OrganizationSignup";

declare global {
  interface Window {
    heap: any;
  }
}

const App: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getIdTokenClaims,
    logout,
  } = useAuth0();
  const { token: authToken, getToken, refreshToken } = useAuthToken();
  const domain =
    process.env.REACT_APP_AUTH0_DOMAIN || "dev-ociu84lf8keizm5i.us.auth0.com";
  const [userMeta, setUserMeta] = useState<any>([]);
  const [affiliated, setAffiliated] = useState<
    { company_name: string; key_value: string }[]
  >([]);
  const [companyId, setCompanyId] = useState<number | null>(() => {
    const savedCompanyId = localStorage.getItem("selectedCompanyId");
    return savedCompanyId ? parseInt(savedCompanyId, 10) : null;
  });

  // Track the current API key - defaults to Auth0 context, but can be overridden with admin key
  const [currentApiKey, setCurrentApiKey] = useState<
    | { getToken: () => Promise<string>; refreshToken: () => Promise<string> }
    | string
    | null
  >(null);
  const [authRedirecting, setAuthRedirecting] = useState(false);

  const isInitialized = useRef(false);
  const affiliatedLoadStateRef = useRef<"idle" | "loading" | "loaded">("idle");

  const authInflightTs = sessionStorage.getItem("auth_inflight_ts");
  const authInflightAge = authInflightTs
    ? Date.now() - parseInt(authInflightTs, 10)
    : null;
  const authInflightRecent =
    authInflightAge !== null && authInflightAge >= 0 && authInflightAge < 15000;
  const authInflightExpired =
    authInflightAge !== null && authInflightAge >= 15000;
  const authCodeParam = searchParams.get("code");
  const hasAuthCode = Boolean(authCodeParam && authCodeParam !== "success");

  // Initialize currentApiKey once authToken is available
  useEffect(() => {
    if (authToken && !isInitialized.current) {
      console.log("Initializing with Auth0 context");
      setCurrentApiKey({ getToken, refreshToken });
      isInitialized.current = true;
    }
  }, [authToken, getToken, refreshToken]);

  // Reset affiliated companies cache on logout.
  useEffect(() => {
    if (!isAuthenticated) {
      affiliatedLoadStateRef.current = "idle";
      setAffiliated([]);
    }
  }, [isAuthenticated]);

  // Wrapper for setUserMeta that also updates the current API key and company ID
  const handleSetUserMeta = (meta: any) => {
    setUserMeta(meta);
    // If meta contains an api_key, update the current API key
    if (meta && meta.api_key) {
      console.log(
        "Switching to API key:",
        typeof meta.api_key === "string" ? "admin key" : "auth context"
      );
      setCurrentApiKey(meta.api_key);

      // If meta contains a company_id, update it (for admin impersonation)
      if (meta.company_id !== undefined) {
        console.log("Updating company ID to:", meta.company_id);
        setCompanyId(meta.company_id);
        if (meta.company_id !== null) {
          localStorage.setItem("selectedCompanyId", meta.company_id.toString());
        }
      }
    } else {
      // If no api_key in meta, reset to Auth0 context
      console.log("Resetting to Auth0 context");
      setCurrentApiKey({ getToken, refreshToken });
    }
  };

  const loadCompanies = (authContext: {
    getToken: () => Promise<string>;
    refreshToken: () => Promise<string>;
  }) => {
    // This endpoint is expensive for internal Maitai users; make it strictly
    // "load once" per app session (unless we explicitly add a refresh control).
    if (affiliatedLoadStateRef.current !== "idle") {
      return;
    }
    affiliatedLoadStateRef.current = "loading";

    CompanyService.getAffiliatedCompanies(null, authContext).subscribe(
      (data: any) => {
        setAffiliated(data);
        affiliatedLoadStateRef.current = "loaded";
        // If we have a saved company ID, verify it's still valid
        const savedCompanyId = localStorage.getItem("selectedCompanyId");
        if (savedCompanyId) {
          const isValidCompany = data.some(
            (company: Company) => company.id === parseInt(savedCompanyId, 10)
          );
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompanyId");
            setCompanyId(null);
          }
        }
      },
      (error: any) => {
        console.error(error);
        affiliatedLoadStateRef.current = "idle";
      }
    );
  };

  /**
   * Returns Auth0 login parameters with organization persistence.
   *
   * Persists the user's Auth0 organization selection across sessions to avoid
   * showing the organization selector every time they visit the site.
   *
   * When a saved organization ID exists and forceSelectOrg is false:
   * - Passes the saved org_id to Auth0
   * - Automatically signs the user into their last used organization
   * - No org selector shown if the user belongs to that org
   *
   * When no org_id is saved or forceSelectOrg is true:
   * - Shows the Auth0 organization selector (if user belongs to multiple orgs)
   * - Supports both org and non-org users
   *
   * The org_id is cleared on logout or if there's an organization-related error.
   */
  const getLoginParams = (forceSelectOrg = false) => {
    const savedOrgId = localStorage.getItem("auth0_org_id");
    const loginHint = localStorage.getItem("auth0_login_hint");
    const baseParams: any = {};

    if (savedOrgId && !forceSelectOrg) {
      console.log(`Using saved organization: ${savedOrgId}`);
      baseParams.organization = savedOrgId;
    } else {
      console.log("No saved organization, showing selector");
    }

    if (loginHint) {
      baseParams.login_hint = loginHint;
    }

    return baseParams;
  };

  const supportSignupSuccess =
    (searchParams.get("supportSignUp") === "true" ||
      searchParams.get("supportForgotPassword") === "true") &&
    searchParams.get("success") === "true" &&
    searchParams.get("code") === "success";

  useEffect(() => {
    if (!supportSignupSuccess || isAuthenticated) {
      return;
    }
    setAuthRedirecting(true);
    loginWithRedirect({ authorizationParams: getLoginParams() });
  }, [
    supportSignupSuccess,
    isAuthenticated,
    loginWithRedirect,
    getLoginParams,
  ]);

  useEffect(() => {
    const invitation = searchParams.get("invitation");
    const organization = searchParams.get("organization");
    const authCode = searchParams.get("code");

    // If Auth0 redirected back with a code, mark inflight to avoid redirect loops while tokens load
    if (authCode) {
      sessionStorage.setItem("auth_inflight_ts", Date.now().toString());
    } else if (authInflightExpired) {
      sessionStorage.removeItem("auth_inflight_ts");
    } else if (!authInflightRecent && authInflightTs) {
      // Clean up stale inflight markers
      sessionStorage.removeItem("auth_inflight_ts");
    }

    if (location.pathname === "/login" && invitation && organization) {
      localStorage.setItem("auth0_org_id", organization);
      loginWithRedirect({
        authorizationParams: {
          organization,
          invitation,
        },
      });
    }
  }, [location, searchParams, loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated) {
      sessionStorage.removeItem("auth_inflight_ts");
    }
    if (isAuthenticated && location.pathname === "/login") {
      const savedPath = localStorage.getItem("auth0_return_path") || "/";
      localStorage.removeItem("auth0_return_path");
      navigate(savedPath);
    }
  }, [isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    if (!authToken || !currentApiKey) {
      const returnPath = `${location.pathname}${location.search}${location.hash}`;
      if (returnPath !== "/" && location.pathname !== "/login") {
        localStorage.setItem("auth0_return_path", returnPath);
      }
    } else if (location.pathname !== "/login") {
      localStorage.removeItem("auth0_return_path");
    }
  }, [
    authToken,
    currentApiKey,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (searchParams.get("logout")) {
      searchParams.delete("logout");
      setSearchParams(searchParams);
      localStorage.removeItem("auth0_org_id");
      localStorage.removeItem("auth0_return_path");
      sessionStorage.removeItem("auth_inflight_ts");
      logout({ logoutParams: { returnTo: window.location.origin } }).then(
        () => {}
      );
    }
    if (isAuthenticated) {
      // Clear any pending switch flags once we're authenticated
      localStorage.removeItem("switching_org");

      if (searchParams.get("logout")) {
        localStorage.removeItem("auth0_org_id");
        localStorage.removeItem("auth0_return_path");
        logout({ logoutParams: { returnTo: window.location.origin } }).then(
          () => {
            loginWithRedirect({ authorizationParams: getLoginParams() });
          }
        );
      }
      // Intercom({
      //   app_id: "lgh3y6pe",
      //   user_id: user?.email,
      //   name: user?.name,
      //   email: user?.email,
      //   created_at: user?.createdAt,
      //   z_index: 1000,
      // });
      window.heap.identify(user?.email);

      // Store the organization ID from Auth0 token if present
      getIdTokenClaims()
        .then((claims) => {
          // Check multiple possible locations for org_id
          const orgId =
            claims?.org_id ||
            (user as any)?.org_id ||
            (user as any)?.["https://auth0.com/org_id"] ||
            (user as any)?.organization_id;
          if (orgId) {
            localStorage.setItem("auth0_org_id", orgId);
          }
        })
        .catch((err) => {
          console.error("Error getting ID token claims:", err);
        });

      // Use JWT token from AuthContext (handles expiration and refresh automatically)
      if (authToken) {
        // Load companies using auth context
        loadCompanies({ getToken, refreshToken });

        // Fetch user profile for metadata (optional, for display purposes)
        fetch(`https://${domain}/api/v2/users/${user?.sub}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        })
          .then((response) => response.json())
          .then((profile) => {
            setUserMeta(profile["user_metadata"]);
          })
          .catch((err) => {
            console.error("Error fetching user profile", err);
          });
      }
    } else if (!isLoading && !authToken && !currentApiKey && !isAuthenticated) {
      if (authInflightRecent && hasAuthCode) {
        return;
      }

      const authError = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");
      const isLoginPage = location.pathname === "/login";
      const isOnboardingPage = location.pathname === "/org-signup";
      const shouldStartLogin = searchParams.get("start") === "1";
      const authAction = searchParams.get("auth_action");
      const forceSelectOrg =
        searchParams.get("switch") === "1" ||
        localStorage.getItem("switching_org");

      if (
        authError === "interaction_required" ||
        errorDescription?.includes("interaction")
      ) {
        localStorage.removeItem("auth0_org_id");
        searchParams.delete("error");
        searchParams.delete("error_description");
        setSearchParams(searchParams);
        loginWithRedirect({ authorizationParams: getLoginParams(true) });
        return;
      }

      if (isLoginPage && shouldStartLogin) {
        if (forceSelectOrg) {
          localStorage.removeItem("switching_org");
        }
        loginWithRedirect({
          authorizationParams: {
            ...getLoginParams(Boolean(forceSelectOrg)),
            screen_hint: authAction === "signup" ? "signup" : undefined,
          },
        });
        return;
      }

      if (!errorDescription && !isLoginPage && !isOnboardingPage) {
        const returnTo = `${location.pathname}${location.search}${location.hash}`;
        if (returnTo !== "/") {
          localStorage.setItem("auth0_return_path", returnTo);
        }
        navigate("/login");
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getIdTokenClaims,
    logout,
    user,
    location,
    navigate,
    authToken,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!userMeta || !userMeta.api_key) {
      // logout({logoutParams: {returnTo: window.location.origin }}).then(() => {
      //     loginWithRedirect();
      // });
    } else {
      // console.log("userMeta", userMeta);
    }
  }, [userMeta, logout]);

  // Update companyId when auth token changes
  useEffect(() => {
    if (authToken) {
      CompanyService.getCompany(null, { getToken, refreshToken }).subscribe(
        (company: Company) => {
          if (company?.id) {
            setCompanyId(company.id);
            localStorage.setItem("selectedCompanyId", company.id.toString());
          }
        },
        (error: any) => {
          console.error("Error fetching company:", error);
        }
      );
    }
  }, [authToken]);

  const handleRefresh = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("error");
    params.delete("error_description");
    setSearchParams(params);
    setAuthRedirecting(true);
    loginWithRedirect({ authorizationParams: getLoginParams() });
  };

  const errorDescription = searchParams.get("error_description");
  const authError = searchParams.get("error");

  const organizationError = errorDescription?.includes("organization");
  const interactionError =
    authError === "interaction_required" ||
    errorDescription?.includes("interaction");

  if (authRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <p className="text-lg font-semibold text-foreground">Loading...</p>
          <p className="text-sm text-muted-foreground">
            One moment while we finish authentication.
          </p>
        </div>
      </div>
    );
  }

  // Don't show error screen for interaction_required - it's handled in the auth flow
  if (errorDescription && !interactionError) {
    if (organizationError) {
      localStorage.removeItem("auth0_org_id");
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-auto text-center space-y-6 p-6">
          <h1 className="text-2xl font-gc-demi-bold text-foreground">
            {organizationError
              ? "Organization not found"
              : "Email Verification"}
          </h1>
          <div className="space-y-4">
            <p className="text-foreground">{errorDescription}</p>
            <p className="text-muted-foreground text-sm">
              {organizationError
                ? "Please contact support@trymaitai.com if you believe this is an error."
                : "Click continue after you've verified your email."}
            </p>
          </div>
          {organizationError ? (
            <Button asChild>
              <a href="mailto:support@trymaitai.com">Contact Support</a>
            </Button>
          ) : (
            <Button onClick={handleRefresh}>Continue</Button>
          )}
        </div>
      </div>
    );
  }

  const shouldShowAuthRedirect =
    location.pathname === "/login" &&
    !errorDescription &&
    (searchParams.get("start") === "1" ||
      (searchParams.get("invitation") && searchParams.get("organization")));

  if (shouldShowAuthRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <p className="text-lg font-semibold text-foreground">Loading...</p>
          <p className="text-sm text-muted-foreground">
            One moment while we finish authentication.
          </p>
        </div>
      </div>
    );
  }

  const savedReturnPath = localStorage.getItem("auth0_return_path");
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const effectiveReturnPath =
    savedReturnPath && savedReturnPath !== "/"
      ? savedReturnPath
      : currentPath !== "/login"
      ? currentPath
      : "/";
  if (location.pathname === "/org-signup" && authInflightRecent) {
    sessionStorage.removeItem("auth_inflight_ts");
  }
  const shouldShowAuthLoading =
    isLoading || (authInflightRecent && location.pathname !== "/org-signup");
  const inflightGuard = hasAuthCode && (!authToken || !currentApiKey);

  if (inflightGuard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <p className="text-lg font-semibold text-foreground">Loading...</p>
          <p className="text-sm text-muted-foreground">
            One moment while we finish authentication.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              sessionStorage.removeItem("auth_inflight_ts");
              const params = new URLSearchParams(searchParams);
              params.delete("code");
              params.delete("state");
              navigate(`/login?${params.toString()}`);
            }}>
            Restart login
          </Button>
        </div>
      </div>
    );
  }

  if (!authToken || !currentApiKey) {
    if (isAuthenticated) {
      return <Navigate to={effectiveReturnPath} replace />;
    }
    if (shouldShowAuthLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="space-y-3 text-center">
            <p className="text-lg font-semibold text-foreground">Loading...</p>
            <p className="text-sm text-muted-foreground">
              One moment while we finish authentication.
            </p>
          </div>
        </div>
      );
    }
    return (
      <Routes>
        <Route path="/login" element={<LoginChooser />} />
        <Route path="/org-signup" element={<OrganizationSignup />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <PermissionsProvider apiKey={currentApiKey} companyId={companyId}>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route
          path="/"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Tutorial />
            </AppLayout>
          }
        />
        <Route
          path="/applications"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Applications />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId"
          element={
            <AppLayout>
              <ApplicationOverview />
            </AppLayout>
          }
        />
        <Route
          path="/application/new"
          element={
            <AppLayout>
              <RegisterApplication />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId/edit"
          element={
            <AppLayout>
              <EditApplication />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId/clone"
          element={
            <AppLayout>
              <CloneApplication />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/sentinel/new"
          element={
            <AppLayout>
              <RegisterSentinel />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/sentinel/:sentinelId"
          element={
            <AppLayout>
              <EditSentinel />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId/actions/new"
          element={
            <AppLayout>
              <ApplicationActionNew />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId/intent/:actionId"
          element={
            <AppLayout>
              <ApplicationActionOverview />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId/agents"
          element={
            <AppLayout>
              <ApplicationAgentsOverview />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId/agent/:agentId/intents"
          element={
            <AppLayout>
              <AgentIntentManagement />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/testsets"
          element={
            <AppLayout>
              <TestSets />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/testsets/:testSetId"
          element={
            <AppLayout>
              <TestSetDetail />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/testsets/new"
          element={
            <AppLayout>
              <TestSetNew />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/testsets/:testSetId/edit"
          element={
            <AppLayout>
              <TestSetEdit />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/testsets/:testSetId/runs/new"
          element={
            <AppLayout>
              <TestRunNew />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/testsets/:testSetId/runs/:testRunId"
          element={
            <AppLayout>
              <TestRunDetail />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId/session/:sessionId"
          element={
            <AppLayout>
              <SessionOverview />
            </AppLayout>
          }
        />
        <Route
          path="/settings"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <DevelopersBase />
            </AppLayout>
          }
        />
        <Route
          path="/settings/webhooks/create"
          element={<DevelopersWebhooksCreate />}
        />
        <Route
          path="/sandbox"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Sandbox />
            </AppLayout>
          }
        />
        <Route
          path="/events"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Events />
            </AppLayout>
          }
        />
        <Route
          path="/billing"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <BillingBase />
            </AppLayout>
          }
        />
        <Route
          path="/admin/annotation"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AnnotationOverview />
            </AppLayout>
          }
        />
        <Route
          path="/admin/annotation/company/:companyId/application/:appId/intent/:intentId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AnnotationView />
            </AppLayout>
          }
        />
        <Route
          path="/admin/email"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <EmailOverview />
            </AppLayout>
          }
        />
        <Route
          path="/application/:applicationId/session/:sessionId/request/:requestId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <RequestOverviewScreen />
            </AppLayout>
          }
        />
        <Route
          path="/admin/sentinel"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <SentinelOverview />
            </AppLayout>
          }
        />
        <Route
          path="/admin/evaluations"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Evaluations />
            </AppLayout>
          }
        />

        <Route
          path="/admin/ingest-data"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <IngestData />
            </AppLayout>
          }
        />
        <Route
          path="/admin/agent-uptime-metrics"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentUptimeMetrics />
            </AppLayout>
          }
        />
        <Route
          path="/admin/instances"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <VastInstancesOverview />
            </AppLayout>
          }
        />
        <Route
          path="/admin/base-model-candidates"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <BaseModelCandidatesOverview />
            </AppLayout>
          }
        />
        <Route
          path="/evaluation"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <EvaluationHome />
            </AppLayout>
          }
        />
        <Route
          path="/evaluation/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <EvaluationNew />
            </AppLayout>
          }
        />
        <Route
          path="/evaluation/:evaluationId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <EvaluationDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/compositions"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Datasets />
            </AppLayout>
          }
        />
        <Route
          path="/forge/compositions/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <CompositionNew />
            </AppLayout>
          }
        />
        <Route
          path="/forge/compositions/:id"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <CompositionDetailView />
            </AppLayout>
          }
        />
        <Route
          path="/forge/compositions/:id/edit"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <CompositionEdit />
            </AppLayout>
          }
        />
        <Route
          path="/forge/datasets/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <DatasetNew />
            </AppLayout>
          }
        />
        <Route
          path="/forge/datasets/:id"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <DatasetDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/datasets/:datasetId/reviews/:reviewId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ReviewDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/datasets/:id/edit"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <DatasetEdit />
            </AppLayout>
          }
        />
        <Route
          path="/forge/datasets/:id/augmentations/:augmentationId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AugmentationDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/finetuning"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <FinetuneRuns />
            </AppLayout>
          }
        />
        <Route
          path="/forge/finetuning/:finetuneRunId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <FinetuneRunDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/finetuning/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <NewFinetuneRun />
            </AppLayout>
          }
        />
        <Route
          path="/forge/finetuning/:finetuneRunId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <FinetuneRunDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/models"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Models />
            </AppLayout>
          }
        />
        <Route
          path="/forge"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ForgeOverview />
            </AppLayout>
          }
        />
        <Route
          path="/forge/agents"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Agents />
            </AppLayout>
          }
        />
        <Route
          path="/forge/workflows"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Workflows />
            </AppLayout>
          }
        />
        <Route
          path="/forge/workflows/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <WorkflowNew />
            </AppLayout>
          }
        />
        <Route
          path="/forge/workflows/:workflowId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <WorkflowDetail />
            </AppLayout>
          }
        />
        <Route
          path="/forge/conversation-trees"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ConversationTrees />
            </AppLayout>
          }
        />
        <Route
          path="/forge/studio"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <StudioOverview />
            </AppLayout>
          }
        />
        <Route
          path="/agent/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentBuilder mode="create" />
            </AppLayout>
          }
        />
        <Route
          path="/forge/conversation-tree"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ConversationTree />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentDetail />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId/edit"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentBuilder mode="edit" />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId/bulk-import"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <BulkAPIImportPage />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId/actions/:actionId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentActionOverview />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId/actions/:actionId/edit"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentActionEditPage />
            </AppLayout>
          }
        />
        <Route
          path="/forge/conversation-trees/:treeId/execute"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ConversationTreeExecution />
            </AppLayout>
          }
        />
        <Route
          path="/admin/validation/overview"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ValidationOverview />
            </AppLayout>
          }
        />
        <Route
          path="/forge/finetuning/:finetuneRunId/validation/:validationRunId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <ValidationRun />
            </AppLayout>
          }
        />
        <Route
          path="/test/test-sets"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <TestSets />
            </AppLayout>
          }
        />
        <Route
          path="/test/workflow-test-sets/:testSetId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <WorkflowTestSetDetail />
            </AppLayout>
          }
        />
        <Route
          path="/test/workflow-test-sets/:testSetId/edit"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <WorkflowTestSetEdit />
            </AppLayout>
          }
        />
        <Route
          path="/test/workflow-test-sets/:testSetId/runs/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <WorkflowTestRunNew />
            </AppLayout>
          }
        />
        <Route
          path="/test/workflow-test-sets/:testSetId/runs/:testRunId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <WorkflowTestRunDetail />
            </AppLayout>
          }
        />
        <Route
          path="/test/test-sets/new"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <TestSetNew />
            </AppLayout>
          }
        />
        <Route
          path="/test/criteria"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <CriteriaPage />
            </AppLayout>
          }
        />
        <Route
          path="/test/evaluations"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <Evaluations />
            </AppLayout>
          }
        />
        <Route
          path="/intent-groups"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <IntentGroupsOverview />
            </AppLayout>
          }
        />
        <Route
          path="/intent-group/:intentGroupId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <IntentGroupOverview />
            </AppLayout>
          }
        />
        <Route
          path="/agent/:agentId/session/:sessionId/task/:taskId"
          element={
            <AppLayout companies={affiliated} setMeta={handleSetUserMeta}>
              <AgentTaskView />
            </AppLayout>
          }
        />
      </Routes>
    </PermissionsProvider>
  );
};

export default () => (
  <ThemeProvider>
    <Router>
      <App />
      <Toaster />
    </Router>
  </ThemeProvider>
);
